import { defineNuxtPlugin } from '#imports'
import { StatusCodes } from 'http-status-codes'
import { BUGSNAG_KEY } from '@shared/utils/constant'
import {
  ErrorType,
  isThrownException,
  GRPC_REQUEST_FAILED,
  TransactionException,
  GrpcUnaryRequestException
} from '@injectivelabs/exceptions'
import { IS_PRODUCTION } from '@/app/utils/constant'
import type { ThrownException } from '@injectivelabs/exceptions'

/**
 * As we conditionally include the nuxt-bugsnag module
 * the type of it can be undefined
 **/
declare let useBugsnag: () => any

const reportToUser = (error: ThrownException) => {
  const notificationStore = useSharedNotificationStore()

  // Timedout requests happening in the background should not be reported to the user
  if (
    error.type === ErrorType.HttpRequest &&
    error.code === StatusCodes.REQUEST_TOO_LONG
  ) {
    return
  }

  if (
    error instanceof GrpcUnaryRequestException &&
    error.contextCode === GRPC_REQUEST_FAILED
  ) {
    return notificationStore.error({
      title: 'The product is experiencing higher than usual demand',
      description:
        'Hang tight, engineers are doing their best to improve the performance and efficiency.'
    })
  }

  if (!(error instanceof TransactionException)) {
    return notificationStore.error({
      context: error.originalMessage,
      title: error.message || 'Something happened'
    })
  }

  console.error(error)

  return notificationStore.error({
    title: error.message,
    context: error.originalMessage
  })
}

const reportToBugSnag = (error: ThrownException) => {
  if (!IS_PRODUCTION) {
    console.warn(error.toCompactError().message)
    console.error(error)

    return
  }

  if ([ErrorType.Unspecified, ErrorType.WalletError].includes(error.type)) {
    console.warn(error.toCompactError().message)
    console.error(error)

    return
  }

  if (BUGSNAG_KEY) {
    const sharedWalletStore = useSharedWalletStore()

    useBugsnag().notify(error, (event: any) => {
      event.errors[0].errorClass = error.errorClass || error.name

      if (sharedWalletStore.isUserConnected) {
        event.setUser(sharedWalletStore.injectiveAddress)
      }

      event.addMetadata('error-context', error.toObject())
    })
  }
}

const reportUnknownErrorToBugsnag = (error: Error) => {
  if (!IS_PRODUCTION) {
    console.error({ error, stack: error.stack })
  }

  const newError = new Error(
    `The ${error.message} is not handled as an Exception - ${error.stack}`
  )

  console.warn(newError.message, newError.stack)

  if (BUGSNAG_KEY) {
    useBugsnag().notify(newError)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()

  nuxtApp.vueApp.config.errorHandler = (error, context) => {
    console.warn(error, context, (error as any).stack)
  }

  router.onError((error, to) => {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
      window.location.href = to.fullPath
    }
  })

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const error = event.reason

    if (!IS_PRODUCTION) {
      return
    }

    if (!isThrownException(error)) {
      reportUnknownErrorToBugsnag(error)
    } else {
      reportToBugSnag(error)
    }
  }

  const errorHandler = (error: ThrownException) => {
    if (!isThrownException(error)) {
      return reportUnknownErrorToBugsnag(error)
    }

    reportToUser(error)

    if (IS_PRODUCTION) {
      reportToBugSnag(error)
    }

    console.warn(error.toObject())
  }

  return {
    provide: {
      onError: errorHandler
    }
  }
})
